import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import {
  PrismBadge,
  PrismBanner,
  PrismButton,
  PrismCard,
  PrismLoadingBar,
  PrismMessage,
} from '@mylighthouse/prism-ember';
import { includes } from '@nullvoxpopuli/ember-composable-helpers';
import { t } from 'ember-intl';
import { and, gt } from 'ember-truth-helpers';
import { IncrementFor } from 'frontend-utils';
import GeneralButtonsButtonExport from 'lumen/components/general/buttons/button-export';
import GeneralDatepickersDatepickerRange from 'lumen/components/general/datepickers/datepicker-range';
import GeneralDatepickersPickerPeriod from 'lumen/components/general/datepickers/picker-period';
import GeneralFilterBarFilterBar from 'lumen/components/general/filter-bar/filter-bar';
import GeneralPageHeader from 'lumen/components/general/page/header';
import GeneralPageMain from 'lumen/components/general/page/main';
import ParityChartsChartParityStrategyEvolution from 'lumen/components/parity/charts/chart-parity-strategy-evolution';
import ParityChartsChartParityStrategyGauge from 'lumen/components/parity/charts/chart-parity-strategy-gauge';
import ParityStrategyBreakdownBlock from 'lumen/components/parity/strategy/breakdown-block';
import ParityTablesTableParityStrategyIssues from 'lumen/components/parity/tables/table-parity-strategy-issues';
import type MarketParityStrategyController from 'lumen/controllers/market/parity/strategy';
import { FILTER_BAR_PAGETYPES } from 'lumen/utils/constants/general-constants';
import { DIMENSIONS } from 'lumen/utils/constants/pi-constant-values';

import styles from './strategy.module.css';

interface MarketParityStrategySignature {
  Args: {
    controller: MarketParityStrategyController;
    model: unknown;
  };
}

<template>
  <GeneralPageHeader class="parity-strategy-header">
    <div class="content-wrapper flex flex-row items-center">
      <GeneralDatepickersDatepickerRange
        @calendarFocusOnEnd={{true}}
        @maxDate={{@controller.maxDateMoment}}
        @minDate={{@controller.minDateMoment}}
        {{! @glint-expect-error: Incorrect type }}
        @range={{@controller.dateRange}}
        @rangeChanged={{@controller.updateRange}}
        @selectStandardRange={{true}}
        @standardRange={{@controller.isoDateAggregation}}
      />
      <div
        class="filter-bar-wrapper"
        {{on "mouseenter" (fn @controller.setIsFilterBarHovered true)}}
        {{on "mouseleave" (fn @controller.setIsFilterBarHovered false)}}
      >
        {{! @glint-expect-error: Incorrect type }}
        <GeneralFilterBarFilterBar
          @activeFilters={{@controller.mappedFilters}}
          @filterBarSegmentationClicked={{@controller.clearActiveFilter}}
          @hideFavourites={{true}}
          @isLoading={{@controller.isBreakdownRequestRunning}}
          @pageType={{FILTER_BAR_PAGETYPES.PARITY_STRATEGY}}
        />
        {{#if
          (and
            @controller.isFilterBarHovered
            (gt @controller.mappedFilters.length 0)
          )
        }}
          <div class="ml-100">
            <PrismButton
              @buttonType="secondary"
              @label={{t "general.remove"}}
              @onClick={{@controller.clearFilters}}
              @size="small"
            />
          </div>
        {{/if}}
      </div>
      <GeneralButtonsButtonExport
        @disabledMessage={{if
          @controller.hasActiveFilters
          (t "parity_strategy.export_reports")
        }}
        @exportAction={{@controller.handleExport}}
        @isDisabled={{@controller.isExportDisabled}}
      />
    </div>
  </GeneralPageHeader>

  <GeneralPageMain class="parity-strategy">
    {{#if @controller.isLoading}}
      <PrismLoadingBar @isFixed={{true}} @positionTop={{120}} />
    {{/if}}

    <div class="content-wrapper main-content grid gap-800">
      <section class="grid gap-400">
        <h2>{{t "parity_analytics.headers.evolution"}}</h2>

        {{#if @controller.hasEvolutionRequestError}}
          <div class="grid place-items-center">
            <PrismMessage
              @description={{t "general.error"}}
              @illustration="100x100_critical"
              @title={{t "general.error_title"}}
            />
          </div>
        {{else}}
          <PrismCard @contentLayout="borderedHeader" @isParent={{true}}>
            <:header as |Header|>
              <Header.title>
                {{t "parity_analytics.chart_title"}}
              </Header.title>
            </:header>

            <:headerAfter>
              <div class="flex items-center justify-end gap-200">
                {{#if @controller.isEvolutionRequestRunning}}
                  <div class="loading-pulse w-[150px]" />
                {{else}}
                  <p
                    class="text-300--regular"
                  >{{@controller.formattedPeriodRange}}</p>
                {{/if}}

                <GeneralDatepickersPickerPeriod
                  {{! @glint-expect-error: Incorrect type }}
                  @beginReached={{@controller.isBeginReached}}
                  @endReached={{@controller.isEndReached}}
                  @historicalExtractDateMoment={{@controller.historicalExtractDateMomentWithFallback}}
                  @periodChanged={{@controller.updateHistoricalExtractDate}}
                  @periodType={{@controller.dateAggregation}}
                />
              </div>
            </:headerAfter>

            <:content>
              <div class={{styles.card-content}}>
                <ParityChartsChartParityStrategyEvolution
                  @customLegendItemClick={{@controller.updateParityFilters}}
                  @data={{@controller.filteredEvolutionsForChart}}
                  @extractDate={{@controller.extractDate}}
                  @isBenchmarkSelected={{@controller.isBenchmarkSelected}}
                  @isLatestSelected={{@controller.isLatestSelected}}
                  @isLoading={{@controller.isEvolutionRequestRunning}}
                  @updateExtractDate={{@controller.updateExtractDate}}
                />
                <ParityChartsChartParityStrategyGauge
                  @isBenchmarkSelected={{@controller.isBenchmarkSelected}}
                  @isLoading={{@controller.isEvolutionRequestRunning}}
                  @previousPeriodData={{@controller.previousPeriodData}}
                  @selectedPeriodData={{@controller.selectedPeriodData}}
                />
              </div>
            </:content>
          </PrismCard>
        {{/if}}
      </section>

      <section class="grid gap-400">
        <h2>{{@controller.formattedExtractRange}}</h2>

        {{#if @controller.hasNoData}}
          <div class="grid place-items-center">
            <PrismMessage
              @description=""
              @illustration="100x100_info"
              @title={{t "parity_analytics.no_data"}}
            />
          </div>
        {{else if @controller.hasBreakdownRequestError}}
          <div class="grid place-items-center">
            <PrismMessage
              @description={{t "general.error"}}
              @illustration="100x100_critical"
              @title={{t "general.error_title"}}
            />
          </div>
        {{else}}
          <h3>
            {{t "general.breakdown"}}
            <span class="text-300--regular">{{t "ranking.vs_last_week"}}</span>
          </h3>

          {{#if @controller.isBreakdownRequestRunning}}
            <div class="insight-grid grid-cols-4">
              <IncrementFor @times={{@controller.BREAKDOWN_BLOCK_ORDER.length}}>
                <ParityStrategyBreakdownBlock @isLoading={{true}} />
              </IncrementFor>
            </div>
          {{else}}
            <div class="insight-grid grid-cols-4">
              {{#each-in
                @controller.breakdownBlocks
                as |breakdownId breakdownBlock|
              }}
                <ParityStrategyBreakdownBlock
                  @addFilter={{@controller.addFilter}}
                  @breakdownData={{breakdownBlock.data}}
                  @breakdownId={{breakdownId}}
                  @breakdownTitle={{breakdownBlock.formattedDimension}}
                  @isSearchEnabled={{includes
                    breakdownId
                    @controller.breakdownsWithSearchEnabled
                  }}
                  @isSelected={{@controller.isSelected}}
                  @maxReferenceValue={{@controller.maxReferenceValue}}
                />
              {{/each-in}}
            </div>
          {{/if}}

          <h3 class="mt-400">
            {{t "parity_analytics.headers.ota_promotions"}}
            <span class="text-300--regular">{{t "ranking.vs_last_week"}}</span>
          </h3>

          {{#if @controller.isBreakdownRequestRunning}}
            <div class="insight-grid grid-cols-4">
              <IncrementFor @times={{@controller.PROMOTION_BLOCK_ORDER.length}}>
                <ParityStrategyBreakdownBlock @isLoading={{true}} />
              </IncrementFor>
            </div>
          {{else}}
            <div class="insight-grid grid-cols-4">
              {{#each-in
                @controller.promotionBlocks
                as |breakdownId breakdownBlock|
              }}
                <ParityStrategyBreakdownBlock
                  @addFilter={{@controller.addFilter}}
                  @breakdownData={{breakdownBlock.data}}
                  @breakdownId={{breakdownId}}
                  @breakdownTitle={{breakdownBlock.formattedDimension}}
                  @isSelected={{@controller.isSelected}}
                  @maxReferenceValue={{@controller.maxReferenceValue}}
                />
              {{/each-in}}
            </div>
          {{/if}}

          {{#if @controller.isHotelSelected}}
            <div class="mt-400 flex items-center gap-200">
              <h3>{{t "general.issues" count=2}}</h3>
              <PrismBadge @label={{@controller.numberOfIssues}} @type="info" />
            </div>

            {{#if @controller.hasIssueRequestError}}
              <div class="grid place-items-center">
                <PrismMessage
                  @description={{t "general.error"}}
                  @illustration="100x100_critical"
                  @title={{t "general.error_title"}}
                />
              </div>
            {{else}}
              <PrismBanner
                @bannerMessage={{t "parity_strategy.issues_table_info_banner"}}
                @bannerStyle="info"
                @icon="circle-information-outline"
              />
              <ParityTablesTableParityStrategyIssues
                @issues={{@controller.issues}}
                @updateStatus={{@controller.updateStatusTask.perform}}
              />
            {{/if}}
          {{else}}
            <div class="mt-400 flex items-center gap-200">
              <h3>{{t "parity_analytics.headers.hotels_with_issues"}}</h3>
              <PrismBadge
                @label={{@controller.numberOfHotelWithIssues}}
                @type="info"
              />
            </div>

            {{#if @controller.isBreakdownRequestRunning}}
              <ParityStrategyBreakdownBlock @isLoading={{true}} />
            {{else}}
              <ParityStrategyBreakdownBlock
                @addFilter={{@controller.addFilter}}
                @breakdownData={{@controller.hotelData}}
                @breakdownId={{DIMENSIONS.HOTEL}}
                @breakdownTitle={{t "general.hotel.plural" count=1}}
                @isSearchEnabled={{true}}
                @isSelected={{@controller.isSelected}}
                @maxReferenceValue={{100}}
              />
            {{/if}}
          {{/if}}
        {{/if}}
      </section>
    </div>
  </GeneralPageMain>

  {{outlet}}
</template> satisfies TOC<MarketParityStrategySignature>;
